import React from "react";
import PropTypes from "prop-types";
import styles from "./SpeakerDetailsForm.module.css";

const noop = () => {};

function toInputField(name, label, placeholder = "N/A") {
  if (!label) {
    return null;
  }

  return { name, label, placeholder };
}

function toLabel(value) {
  return String(value || "").trim();
}

function extractLabelEntries(source, kind) {
  if (!source || typeof source !== "object") {
    return [];
  }

  const pattern =
    kind === "fact" ? /^Fact(\d+)Label$/i : /^FinalFact(\d+)Label$/i;
  const sourcePrefix = kind === "fact" ? "fact" : "finalFact";

  return Object.keys(source)
    .map((key) => {
      const match = key.match(pattern);
      if (!match) {
        return null;
      }

      const label = toLabel(source[key]);
      if (!label) {
        return null;
      }

      const index = Number(match[1]);
      return {
        index,
        sourceKey: `${sourcePrefix}${index}`,
        label,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.index - right.index);
}

function singleFieldNameForSource(sourceKey) {
  const legacyMap = {
    fact1: "startedBusiness",
    fact2: "wentDiamond",
    fact3: "hometownA",
    fact4: "previousOccupationA",
    finalFact1: "funFact",
  };

  return legacyMap[sourceKey] || sourceKey;
}

function pairFieldNamesForSource(sourceKey) {
  const legacyMap = {
    fact3: { nameA: "hometownA", nameB: "hometownB" },
    fact4: { nameA: "previousOccupationA", nameB: "previousOccupationB" },
    member1_fact1: {
      nameA: "previousOccupationA",
      nameB: "previousOccupationB",
    },
  };

  if (legacyMap[sourceKey]) {
    return legacyMap[sourceKey];
  }

  const safeKey = String(sourceKey || "").replace(/[^a-zA-Z0-9_]/g, "_");
  return {
    nameA: `${safeKey}A`,
    nameB: `${safeKey}B`,
  };
}

function memberFactEntries(memberDefinitions = []) {
  return memberDefinitions
    .map((member, memberIndex) => {
      const facts = extractLabelEntries(member, "fact").map((entry) => ({
        ...entry,
        sourceKey: `member${memberIndex + 1}_${entry.sourceKey}`,
      }));

      const finals = extractLabelEntries(member, "final").map((entry) => ({
        ...entry,
        sourceKey: `member${memberIndex + 1}_${entry.sourceKey}`,
      }));

      return [...facts, ...finals];
    })
    .flat();
}

function getDynamicFieldConfig(contributorType, imageType) {
  const isCouple = imageType === "couple";
  const type = contributorType || {};
  const memberDefinitions = Array.isArray(type.memberDefinitions)
    ? type.memberDefinitions
    : [];

  const topFacts = extractLabelEntries(type, "fact");
  const finalFacts = extractLabelEntries(type, "final");
  const memberFacts = memberFactEntries(memberDefinitions);

  const hasFinalFact1 = finalFacts.some(
    (entry) => entry.sourceKey === "finalFact1",
  );
  const fact5Entry = topFacts.find((entry) => entry.sourceKey === "fact5");

  const normalizedTopFacts = topFacts.filter(
    (entry) => entry.sourceKey !== "fact5",
  );

  const normalizedFinalFacts = hasFinalFact1
    ? finalFacts
    : fact5Entry
    ? [
        { index: 1, sourceKey: "finalFact1", label: fact5Entry.label },
        ...finalFacts,
      ]
    : finalFacts;

  const singleFields = [];
  const pairFields = [];
  const finalFields = normalizedFinalFacts
    .map((entry) =>
      toInputField(singleFieldNameForSource(entry.sourceKey), entry.label),
    )
    .filter(Boolean);

  if (isCouple) {
    // Step 1: Add shared facts and fact1/fact2 as single fields
    const coupleSingles = normalizedTopFacts.filter(
      (entry) =>
        entry.sourceKey === "fact1" ||
        entry.sourceKey === "fact2" ||
        entry.sourceKey === "fact3",
    );

    coupleSingles.forEach((entry) => {
      const field = toInputField(
        singleFieldNameForSource(entry.sourceKey),
        entry.label,
      );
      if (field) {
        singleFields.push(field);
      }
    });

    // Step 2: Add remaining top facts (fact3+) as pair fields
    const combinedTopPairs = normalizedTopFacts
      .filter(
        (entry) =>
          entry.sourceKey !== "fact1" &&
          entry.sourceKey !== "fact2" &&
          entry.sourceKey !== "fact3",
      )
      .map((entry) => {
        const pairNames = pairFieldNamesForSource(entry.sourceKey);
        return {
          label: entry.label,
          mode: "split",
          fieldA: { name: pairNames.nameA, placeholder: "N/A" },
          fieldB: { name: pairNames.nameB, placeholder: "N/A" },
        };
      });

    pairFields.push(...combinedTopPairs);

    // Step 3: Add member-based pair fields (Previous Occupation, etc.)
    const memberBasedPairFields = memberFacts.map((entry) => {
      const pairNames = pairFieldNamesForSource(entry.sourceKey);
      return {
        label: entry.label,
        mode: "split",
        fieldA: { name: pairNames.nameA, placeholder: "N/A" },
        fieldB: { name: pairNames.nameB, placeholder: "N/A" },
      };
    });

    pairFields.push(...memberBasedPairFields);
  } else {
    [...normalizedTopFacts, ...memberFacts].forEach((entry) => {
      const field = toInputField(
        singleFieldNameForSource(entry.sourceKey),
        entry.label,
      );
      if (field) {
        singleFields.push(field);
      }
    });
  }

  const allSingleFields = singleFields;

  return {
    singleFields: allSingleFields,
    pairFields,
    finalFields,
  };
}

export function SpeakerDetailsForm({
  startedBusiness,
  wentDiamond,
  hometownA,
  hometownB,
  previousOccupationA,
  previousOccupationB,
  funFact,
  onChange,
  embedded,
  imageType,
  contributorType,
  ...dynamicDetails
}) {
  const isCouple = imageType === "couple";
  const { singleFields, pairFields, finalFields } = getDynamicFieldConfig(
    contributorType,
    imageType,
  );

  const hasFields = singleFields.length > 0 || pairFields.length > 0 || finalFields.length > 0;

  if (!hasFields) {
    return null;
  }

  const values = {
    startedBusiness,
    wentDiamond,
    hometownA,
    hometownB,
    previousOccupationA,
    previousOccupationB,
    funFact,
    ...dynamicDetails,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
    if (isCouple && name === "hometownA") {
      onChange("hometownB", value);
    }
  };

  const renderInput = (field) => (
    <div key={field.name} className={styles.fieldRow}>
      <label
        className={`${styles.label} ${field.hiddenLabel ? styles.srOnly : ""}`}
        htmlFor={field.name}
      >
        {field.label}
      </label>
      <input
        id={field.name}
        name={field.name}
        type="text"
        value={
          values[field.name] ||
          (isCouple && field.name === "hometownA" ? values.hometownB : "")
        }
        placeholder={field.placeholder}
        className={styles.input}
        onChange={handleChange}
      />
    </div>
  );

  const renderPairRow = ({ label, fieldA, fieldB, mode }) => {
    return (
      <div key={label} className={styles.pairGroup}>
        <span className={styles.pairGroupLabel}>{label}</span>
        <div className={styles.pairDivider} />
        <div className={styles.pairRow}>
          <div className={styles.pairCol}>
            <label className={styles.label} htmlFor={fieldA.name}>
              Speaker A
            </label>
            <input
              id={fieldA.name}
              name={fieldA.name}
              type="text"
              value={values[fieldA.name] || ""}
              placeholder={fieldA.placeholder}
              className={styles.input}
              onChange={handleChange}
            />
          </div>
          <div className={styles.pairCol}>
            <label className={styles.label} htmlFor={fieldB.name}>
              Speaker B
            </label>
            <input
              id={fieldB.name}
              name={fieldB.name}
              type="text"
              value={values[fieldB.name] || ""}
              placeholder={fieldB.placeholder}
              className={styles.input}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className={`${styles.card} ${embedded ? styles.embedded : ""}`}>
      <div className={styles.headerWrap}>
        <h3 className={styles.title}>Speaker Details</h3>
      </div>

      <div className={styles.divider} />

      <div className={styles.fields}>
        {singleFields.map(renderInput)}
        {pairFields.map(renderPairRow)}
        {finalFields.map(renderInput)}
      </div>
    </section>
  );
}

SpeakerDetailsForm.propTypes = {
  startedBusiness: PropTypes.string,
  wentDiamond: PropTypes.string,
  hometownA: PropTypes.string,
  hometownB: PropTypes.string,
  previousOccupationA: PropTypes.string,
  previousOccupationB: PropTypes.string,
  funFact: PropTypes.string,
  onChange: PropTypes.func,
  embedded: PropTypes.bool,
  imageType: PropTypes.string,
  contributorType: PropTypes.shape({
    contributorTypeCode: PropTypes.string,
    contributorType: PropTypes.string,
    fact1Label: PropTypes.string,
    fact2Label: PropTypes.string,
    fact3Label: PropTypes.string,
    fact4Label: PropTypes.string,
    fact5Label: PropTypes.string,
    finalFact1Label: PropTypes.string,
    memberDefinitions: PropTypes.arrayOf(
      PropTypes.shape({
        fact1Label: PropTypes.string,
      }),
    ),
  }),
};

SpeakerDetailsForm.defaultProps = {
  startedBusiness: "",
  wentDiamond: "",
  hometownA: "",
  hometownB: "",
  previousOccupationA: "",
  previousOccupationB: "",
  funFact: "",
  onChange: noop,
  embedded: false,
  imageType: "individual",
  contributorType: null,
};

export default SpeakerDetailsForm;
